import { ChangeDetectionStrategy, Component, inject, input, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Geometry } from 'geojson';
import { firstValueFrom } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { PaginationFooterTemplateComponent } from '@connect-services/client/components/pagination-footer-template/pagination-footer-template.component';
import { NavigationStateService } from '@connect-services/client/guards/reload.guard';
import { RouteNameEnum, RoutePathEnum } from '@connect-services/client/models/routes.model';
import { ContentLayoutService } from '@connect-services/client/services/content-layout.service';
import { EditEntityDetailService } from '@connect-services/client/services/edit-entity-detail.service';
import { EntityDetailCreationContextService } from '@connect-services/client/services/entity-detail-creation-context.service';
import { MapCreationGeomService } from '@connect-services/client/services/map-creation-geom.service';
import { MapEditModeService } from '@connect-services/client/services/map-edit-mode.service';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import {
    CustomColumn,
    CustomTableComponent,
    SortState,
} from '@connect-services/src/lib/components/table/table.component';
import {
    DepositsService,
    LayerControllerGetEntities200ResponseAllOfItemsInner,
    LayerRelation,
    LayersService,
} from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { defaultPaginationParameters, OrderEnum, PaginationParameters } from '@shared/client/models/pagination.model';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, CustomTableComponent, PaginationFooterTemplateComponent, RessourcesModule],
    selector: 'app-related-entities-list',
    templateUrl: './related-entities-list.component.html',
})
export class RelatedEntitiesListComponent implements OnInit {
    public readonly layerRelation = input.required<LayerRelation>();
    public readonly sourceEntityId = input.required<string>();
    public readonly sourceGeometry = input<Geometry | null>(null);
    public readonly sourceLayerId = input.required<string>();
    public readonly sourceTableName = input.required<string>();

    protected readonly columns: CustomColumn[] = [
        { key: 'entity', label: 'entity', sortable: true },
        { key: 'action', label: 'action', sortable: false, textPosition: 'justify-end' },
    ];
    protected readonly hasRelatedEntities = signal<boolean>(false);
    protected readonly paginationParameters = signal<PaginationParameters>({ ...defaultPaginationParameters, take: 5 });
    protected readonly relatedEntities = signal<LayerControllerGetEntities200ResponseAllOfItemsInner[]>([]);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly ButtonType = ButtonType;
    protected readonly projectEditModeService = inject(ProjectEditModeService);

    private readonly searchValue = signal<string>('');

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly contentLayoutService = inject(ContentLayoutService);
    private readonly depositsService = inject(DepositsService);
    private readonly editEntityDetailService = inject(EditEntityDetailService);
    private readonly entityDetailCreationContextService = inject(EntityDetailCreationContextService);
    private readonly layersService = inject(LayersService);
    private readonly mapCreationGeomService = inject(MapCreationGeomService);
    private readonly mapEditModeService = inject(MapEditModeService);
    private readonly navigationService = inject(NavigationStateService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly router = inject(Router);
    private readonly snackbarService = inject(SnackBarService);

    public async ngOnInit(): Promise<void> {
        await this.fetchRelatedEntities('');
        this.hasRelatedEntities.set(!!this.relatedEntities().length);
    }

    protected copyBusinessIdentifier(businessIdentifier: string): void {
        void navigator.clipboard.writeText(businessIdentifier);
        this.snackbarService.openInfoSnackBar('businessIdentifierCopy');
    }

    protected async createRelatedEntity(): Promise<void> {
        const targetLayer = this.layerRelation().relatedLayer;
        const project = this.projectBloc.value();

        if (!targetLayer?.deposit || !project) {
            return;
        }

        try {
            const sourceAttributes = this.getSourceAttributes();

            const deposit = await firstValueFrom(
                this.depositsService.depositControllerGetDepositWithFolderAndStandard({ id: targetLayer.deposit }),
            );

            const entityId = uuidv4();

            this.mapCreationGeomService.setCreationEntityRelatedData(
                deposit,
                this.sourceLayerId(),
                this.sourceTableName(),
                targetLayer,
                entityId,
                sourceAttributes,
            );

            if (this.contentLayoutService.isDataTableView()) {
                this.entityDetailCreationContextService.initFromTabular({
                    depositIds: [deposit.id],
                    entityId,
                    layerIds: [targetLayer.id],
                });
            } else {
                this.entityDetailCreationContextService.initFromMap({
                    depositIds: [deposit.id],
                    entityId,
                    layerIds: [targetLayer.id],
                });
            }
            this.navigationService.markNavigated();
            this.mapEditModeService.activeEditLayerIds.set(new Set([targetLayer.id]));

            await this.router.navigate([
                RoutePathEnum.PROJECTS,
                project.id,
                RoutePathEnum.LAYERS,
                targetLayer.id,
                RouteNameEnum.ENTITY_DETAIL,
                entityId,
                'CREATE',
            ]);
        } catch {}
    }

    protected async fetchRelatedEntities(searchValue: string): Promise<void> {
        const layer = this.layerRelation()?.relatedLayer;

        if (!layer) {
            return;
        }

        const relatedEntities = await firstValueFrom(
            this.layersService.layerControllerGetRelatedLayersEntity({
                editSessionId: this.projectEditModeService.getEditSession()?.id,
                entityId: this.sourceEntityId(),
                order: this.paginationParameters().order,
                orderBy: layer.businessIdentifier ?? 'uuid',
                page: this.paginationParameters().page,
                searchValue,
                sourceLayerId: this.sourceLayerId(),
                take: 5,
                targetLayerId: layer.id,
            }),
        );

        this.paginationParameters.update((currentPaginationParameters: PaginationParameters) => {
            return { ...currentPaginationParameters, totalItems: relatedEntities.totalItems };
        });

        this.relatedEntities.set(relatedEntities.items);
    }

    protected async onPageChange(page: number): Promise<void> {
        this.paginationParameters.update((currentPaginationParameters: PaginationParameters) => {
            return { ...currentPaginationParameters, page };
        });
        await this.fetchRelatedEntities(this.searchValue());
    }

    protected async onSearch(searchValue: string): Promise<void> {
        this.searchValue.set(searchValue);
        await this.fetchRelatedEntities(searchValue);
    }

    protected async onSortChange(sortEvent: SortState | undefined): Promise<void> {
        this.paginationParameters.update((currentPaginationParameters: PaginationParameters) => {
            return { ...currentPaginationParameters, order: sortEvent?.order ?? OrderEnum.DESC };
        });
        await this.fetchRelatedEntities(this.searchValue());
    }

    private getSourceAttributes(): Record<string, unknown> {
        return {
            ...this.editEntityDetailService.bookmarkedAttributesForm()?.value,
            ...this.editEntityDetailService.notBookmarkedAttributesForm()?.value,
            geom: this.sourceGeometry() ?? undefined,
        };
    }
}
